/**
 * Shared display formatters.
 *
 * These were previously re-declared in a dozen components with small, silent
 * differences — one `fmtDuration` returned `—` for null while another rendered
 * `NaNs`, two `fmtPrice` helpers disagreed on decimals. Same-looking numbers
 * formatted three ways across one screen is a correctness problem, not a tidiness
 * one, so the rules live here once.
 *
 * Convention: every function accepts null/undefined and renders an em dash for it.
 * A genuine zero always renders as zero — "no data" and "zero" are different facts.
 */

export const EM_DASH = '—';

// ── Numbers ──────────────────────────────────────────────────────

/** Locale-grouped number with fixed decimals. `1234.5 → "1,235"` at dp 0. */
export const num = (v: number | null | undefined, dp = 0): string =>
  v == null
    ? EM_DASH
    : v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

/** Plain fixed-decimal, no grouping — for tabular columns where the separator adds noise. */
export const fixed = (v: number | null | undefined, dp = 2): string =>
  v == null ? EM_DASH : v.toFixed(dp);

/** Signed currency: `+$12.34` / `-$5.00`. The sign is explicit because a P&L column
 *  where losses are the only signed values reads as if gains were unsigned magnitudes. */
export const money = (v: number | null | undefined, dp = 2): string =>
  v == null ? EM_DASH : `${v >= 0 ? '+' : '-'}$${Math.abs(v).toFixed(dp)}`;

/** Unsigned currency, for balances and totals where a sign would be noise. */
export const currency = (v: number | null | undefined, dp = 2): string =>
  v == null ? EM_DASH : `$${v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

/** Compact currency for dense tables: `$1.2k`, `$980`. */
export const compactMoney = (v: number | null | undefined): string => {
  if (v == null) return EM_DASH;
  const abs = Math.abs(v);
  return abs >= 1000 ? `${v < 0 ? '-' : ''}$${(abs / 1000).toFixed(1)}k` : `${v < 0 ? '-' : ''}$${abs.toFixed(0)}`;
};

/** Signed percentage: `+12.3%`. */
export const pct = (v: number | null | undefined, dp = 1): string =>
  v == null ? EM_DASH : `${v >= 0 ? '+' : ''}${v.toFixed(dp)}%`;

/** Unsigned percentage, for rates that cannot be negative (win rate, drawdown %). */
export const rate = (v: number | null | undefined, dp = 1): string =>
  v == null ? EM_DASH : `${v.toFixed(dp)}%`;

/** Profit factor and similar ratios, where an infinite value is meaningful
 *  (all wins, no losses) rather than an error. */
export const ratio = (v: number | null | undefined, dp = 2): string =>
  v == null ? EM_DASH : isFinite(v) ? v.toFixed(dp) : '∞';

// ── Durations ────────────────────────────────────────────────────

/** Coarse hold time: `45s`, `12m`, `3h 20m`. Null or negative = no data. */
export const duration = (secs: number | null | undefined): string => {
  if (secs == null || secs < 0) return EM_DASH;
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
};

/** Single-unit hold time for narrow columns: `45s`, `12m`, `3.3h`. */
export const durationShort = (secs: number | null | undefined): string => {
  if (secs == null || secs < 0) return EM_DASH;
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
};

/** Two-unit hold time for headline stats: `3h 20m`, `12m 5s`. */
export const durationRich = (secs: number | null | undefined): string => {
  if (secs == null || secs < 0) return EM_DASH;
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r > 0 ? `${m}m ${r}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// ── Dates ────────────────────────────────────────────────────────

export const dateOnly = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString() : EM_DASH;

export const dateTime = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString() : EM_DASH;

/**
 * US market time, which is the only frame in which a US equity trade timestamp means
 * anything: 09:31 ET is the open regardless of where the viewer sits. Rendering these
 * in the browser's local zone silently moves every trade by the UTC offset.
 */
export const nyTime = (isoUtc: string | null | undefined, withSeconds = false): string => {
  if (!isoUtc) return EM_DASH;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
    ...(withSeconds ? { second: '2-digit' as const } : {}),
  }).format(new Date(isoUtc)).replace(',', '');
};

// ── Instrument-aware prices ──────────────────────────────────────

const FX_CCY = new Set([
  'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'NZD', 'CAD', 'CHF',
  'SEK', 'NOK', 'DKK', 'SGD', 'HKD', 'MXN', 'ZAR', 'TRY', 'CNH', 'PLN', 'HUF', 'CZK',
]);

/** True for a 6-letter symbol whose halves are both currency codes (EURUSD, USDJPY). */
export const isForexSymbol = (symbol: string): boolean =>
  /^[A-Za-z]{6}$/.test(symbol)
  && FX_CCY.has(symbol.slice(0, 3).toUpperCase())
  && FX_CCY.has(symbol.slice(3, 6).toUpperCase());

/**
 * Price in the convention its instrument actually uses: FX pairs are rates with no
 * currency symbol (5dp, or 3dp for JPY crosses), everything else is dollars. Showing
 * `$1.08` for EURUSD is not a cosmetic slip — it reads as a price per unit.
 */
export const price = (v: number | null | undefined, symbol: string): string => {
  if (v == null) return EM_DASH;
  if (!isForexSymbol(symbol)) return `$${v.toFixed(2)}`;
  return v.toFixed(symbol.toUpperCase().includes('JPY') ? 3 : 5);
};
